import React, { useEffect, useState } from 'react';
import { AlertTriangle, CalendarClock, Check, Circle, Clock, Link2, PauseCircle, User, X, XCircle } from 'lucide-react';
import {
  LST_CATEGORY_LABELS,
  LST_CATEGORY_STYLES,
  LST_FREQUENCY_LABELS,
  LST_STATUS_LABELS,
  type LstCategory,
  type LstExecucao,
  type LstStatus,
  type LstTemplate,
} from '../types';
import { todayISO } from '../services/lstService';
import { formatTimeHHmm } from '../services/lstMatrixHourly';
import { resolveTemplateIndicator } from '../utils/lstIndicator';
import type { Indicator } from '../../../types/indicator';

interface ExecucaoCellDrawerProps {
  open: boolean;
  onClose: () => void;
  template: LstTemplate | null;
  code: string | null;
  date: string | null;
  execucao: LstExecucao | null;
  executorName: string | null;
  indicators?: Indicator[];
  savingIndicator?: boolean;
  onSaveIndicatorValue?: (value: number) => Promise<boolean>;
  onOpenAction?: (actionId: string) => void;
}

const WEEKDAY_FULL = ['Domingo', 'Segunda', 'Terça', 'Quarta', 'Quinta', 'Sexta', 'Sábado'];

function formatLongDate(iso: string): string {
  const [y, m, d] = iso.split('-').map(Number);
  const date = new Date(y, m - 1, d);
  return `${WEEKDAY_FULL[date.getDay()]}, ${String(d).padStart(2, '0')}/${String(m).padStart(2, '0')}/${y}`;
}

function formatTimestamp(iso: string | null | undefined): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/** Estado visual derivado quando não há execução registrada. */
function derivedState(date: string): 'planejado' | 'perdido' {
  return date > todayISO() ? 'planejado' : 'perdido';
}

const STATUS_BADGE: Record<LstStatus, { icon: React.ReactNode; cls: string }> = {
  realizado: {
    icon: <Check size={13} strokeWidth={3} />,
    cls: 'bg-emerald-50 text-emerald-600 dark:bg-emerald-900/20 dark:text-emerald-400',
  },
  realizado_com_desvio: {
    icon: <AlertTriangle size={12} strokeWidth={2.5} />,
    cls: 'bg-amber-50 text-amber-600 dark:bg-amber-900/20 dark:text-amber-400',
  },
  nao_realizado: {
    icon: <XCircle size={13} strokeWidth={2.5} />,
    cls: 'bg-red-50 text-red-600 dark:bg-red-900/20 dark:text-red-400',
  },
  processo_parado: {
    icon: <PauseCircle size={13} strokeWidth={2.5} />,
    cls: 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400',
  },
};

/** Painel lateral retrátil — detalhe de UMA execução (interseção atividade × dia). */
export const ExecucaoCellDrawer: React.FC<ExecucaoCellDrawerProps> = ({
  open,
  onClose,
  template,
  code,
  date,
  execucao,
  executorName,
  indicators = [],
  savingIndicator = false,
  onSaveIndicatorValue,
  onOpenAction,
}) => {
  const [indicatorValor, setIndicatorValor] = useState('');

  useEffect(() => {
    if (!open) return;
    setIndicatorValor(
      execucao?.indicator_realized_value != null && Number.isFinite(execucao.indicator_realized_value)
        ? String(execucao.indicator_realized_value)
        : '',
    );
    const onEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onEsc);
    return () => document.removeEventListener('keydown', onEsc);
  }, [open, onClose, execucao?.indicator_realized_value]);

  if (!open || !template || !date) return null;

  const today = todayISO();
  const category: LstCategory = template.category ?? 'apoio';
  const recordedAt = formatTimestamp(execucao?.updated_at ?? execucao?.created_at);
  const derived = !execucao ? derivedState(date) : null;

  const badge = execucao ? STATUS_BADGE[execucao.status] : null;
  const indicatorMeta = resolveTemplateIndicator(template, indicators);
  const canEditIndicator = indicatorMeta.hasLink && date <= today && !!onSaveIndicatorValue;
  const parsedIndicator = Number(indicatorValor.replace(',', '.'));
  const indicatorInputValid = Number.isFinite(parsedIndicator);

  const handleSaveIndicator = async () => {
    if (!canEditIndicator || !indicatorInputValid || !onSaveIndicatorValue) return;
    await onSaveIndicatorValue(parsedIndicator);
  };

  return (
    <div className="fixed inset-0 z-[9998] flex justify-end">
      <button type="button" aria-label="Fechar" onClick={onClose} className="absolute inset-0 bg-slate-900/40 backdrop-blur-[1px]" />

      <aside
        role="dialog"
        aria-modal="true"
        className="relative flex h-full w-full max-w-[400px] flex-col border-l border-slate-200 bg-white shadow-2xl dark:border-slate-800 dark:bg-slate-950"
        style={{ animation: 'slideInRight .18s ease-out' }}
      >
        {/* Cabeçalho */}
        <div className={`flex shrink-0 items-start gap-3 border-b border-l-4 border-b-slate-200 px-4 py-3 dark:border-b-slate-800 ${LST_CATEGORY_STYLES[category].border}`}>
          <div className="min-w-0 flex-1">
            <div className="mb-0.5 flex items-center gap-2">
              {code && (
                <span className="inline-flex h-5 items-center rounded bg-slate-100 px-1.5 text-[10px] font-bold text-slate-600 dark:bg-slate-800 dark:text-slate-300">
                  {code}
                </span>
              )}
              <span className="text-[9px] font-bold uppercase tracking-[0.08em] text-slate-400">Raio-X da Execução</span>
            </div>
            <h3 className="text-[14.5px] font-bold leading-tight text-slate-800 dark:text-slate-100">{template.title}</h3>
            <p className="mt-0.5 text-[11px] text-slate-500 dark:text-slate-400">{formatLongDate(date)}</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="shrink-0 rounded-md p-1 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-600 dark:hover:bg-slate-800"
            title="Fechar"
          >
            <X size={18} />
          </button>
        </div>

        {/* Status */}
        <div className="flex shrink-0 items-center gap-2 border-b border-slate-200 px-4 py-3 dark:border-slate-800">
          {execucao && badge ? (
            <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-bold ${badge.cls}`}>
              {badge.icon}
              {LST_STATUS_LABELS[execucao.status]}
            </span>
          ) : derived === 'planejado' ? (
            <span className="inline-flex items-center gap-1.5 rounded-full bg-slate-100 px-2.5 py-1 text-[11px] font-bold text-slate-500 dark:bg-slate-800 dark:text-slate-400">
              <Circle size={11} /> Planejado
            </span>
          ) : (
            <span className="inline-flex items-center gap-1.5 rounded-full bg-red-50 px-2.5 py-1 text-[11px] font-bold text-red-600 dark:bg-red-900/20 dark:text-red-400">
              <XCircle size={12} /> Sem registro
            </span>
          )}
        </div>

        {/* Detalhes */}
        <div className="min-h-0 flex-1 overflow-y-auto px-4 py-3">
          <dl className="space-y-3">
            <Field
              icon={<Clock size={14} />}
              label="Horário previsto"
              value={formatTimeHHmm(template.scheduled_time) ?? '—'}
            />
            {indicatorMeta.hasLink && !canEditIndicator && (
              <Field
                icon={<Link2 size={14} />}
                label="Indicador · Realizado"
                value={
                  execucao?.indicator_realized_value != null
                    ? `${execucao.indicator_realized_value}${indicatorMeta.unit} (meta ${indicatorMeta.target ?? '—'}${indicatorMeta.unit})`
                    : `Pendente — ${indicatorMeta.name ?? 'Indicador'}`
                }
              />
            )}
            <Field
              icon={<CalendarClock size={14} />}
              label="Registrado em"
              value={recordedAt ?? 'Sem registro'}
            />
            <Field
              icon={<User size={14} />}
              label="Executado por"
              value={execucao ? executorName ?? '—' : '—'}
            />
            <Field
              icon={
                <span className={`h-3.5 w-1 rounded-full ${LST_CATEGORY_STYLES[category].dot}`} />
              }
              label="Categoria · Frequência"
              value={`${LST_CATEGORY_LABELS[category]} · ${LST_FREQUENCY_LABELS[template.frequency]}`}
            />
          </dl>

          {canEditIndicator && (
            <div className="mt-4 rounded-md border border-blue-100 bg-blue-50/40 p-3 dark:border-blue-900/40 dark:bg-blue-900/10">
              <p className="mb-2 text-[11px] text-slate-600 dark:text-slate-300">
                <strong className="text-slate-800 dark:text-slate-100">{indicatorMeta.name}</strong>
                {indicatorMeta.target != null && (
                  <span className="text-slate-500">
                    {' '}
                    · Meta: {indicatorMeta.target}
                    {indicatorMeta.unit}
                  </span>
                )}
              </p>
              <label className="mb-1 block text-[10px] font-bold uppercase tracking-wide text-slate-500">
                Informe o valor Realizado <span className="text-red-500">*</span>
              </label>
              <input
                type="number"
                step="any"
                value={indicatorValor}
                onChange={(e) => setIndicatorValor(e.target.value)}
                className="h-9 w-full rounded-md border border-slate-200 px-3 text-[13px] tabular-nums text-slate-700 outline-none focus:border-blue-500 dark:border-slate-700 dark:bg-slate-900/40 dark:text-slate-200"
              />
              <button
                type="button"
                disabled={!indicatorInputValid || savingIndicator}
                onClick={handleSaveIndicator}
                className="mt-2 inline-flex w-full items-center justify-center rounded-md bg-[#2F6FED] px-3 py-2 text-[12px] font-semibold text-white disabled:cursor-not-allowed disabled:opacity-40"
              >
                {savingIndicator ? 'Salvando…' : 'Salvar valor realizado'}
              </button>
            </div>
          )}

          {/* Justificativa */}
          <div className="mt-4">
            <span className="mb-1 block text-[10px] font-bold uppercase tracking-wide text-slate-400">
              Justificativa do desvio
            </span>
            {execucao?.justificativa_desvio ? (
              <p className="rounded-md border border-amber-200 bg-amber-50/70 px-3 py-2 text-[12px] leading-relaxed text-amber-800 dark:border-amber-900/50 dark:bg-amber-900/15 dark:text-amber-200">
                {execucao.justificativa_desvio}
              </p>
            ) : (
              <p className="rounded-md border border-dashed border-slate-200 px-3 py-2 text-[11px] text-slate-400 dark:border-slate-700">
                {execucao?.status === 'realizado'
                  ? 'Rotina realizada dentro do padrão.'
                  : 'Nenhuma justificativa registrada.'}
              </p>
            )}
          </div>

          {/* Descrição da rotina */}
          {template.description && (
            <div className="mt-4">
              <span className="mb-1 block text-[10px] font-bold uppercase tracking-wide text-slate-400">
                Padrão da atividade
              </span>
              <p className="text-[11.5px] leading-relaxed text-slate-500 dark:text-slate-400">{template.description}</p>
            </div>
          )}
        </div>

        {/* Rodapé: ação vinculada */}
        {execucao?.action_id && onOpenAction && (
          <div className="shrink-0 border-t border-slate-200 px-4 py-3 dark:border-slate-800">
            <button
              type="button"
              onClick={() => onOpenAction(execucao.action_id as string)}
              className="inline-flex w-full items-center justify-center gap-1.5 rounded-md border border-blue-200 bg-blue-50 px-3 py-2 text-[12px] font-semibold text-blue-700 transition-colors hover:bg-blue-100 dark:border-blue-900/50 dark:bg-blue-900/20 dark:text-blue-300"
            >
              <Link2 size={13} /> Abrir pendência na Central de Ações
            </button>
          </div>
        )}
      </aside>
    </div>
  );
};

const Field: React.FC<{ icon: React.ReactNode; label: string; value: string }> = ({ icon, label, value }) => (
  <div className="flex items-center gap-2.5">
    <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-400">
      {icon}
    </span>
    <div className="min-w-0">
      <dt className="text-[9px] font-semibold uppercase tracking-wide text-slate-400">{label}</dt>
      <dd className="truncate text-[12.5px] font-medium text-slate-700 dark:text-slate-200">{value}</dd>
    </div>
  </div>
);

export default ExecucaoCellDrawer;
